import React, { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Education,
  createEducation,
  updateEducation,
  deleteEducation,
  updateEducationOrder,
} from "../firebase/educationService";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";
import { educationQuery } from "../queries/contentQueries";

interface EducationContextType {
  education: Education[];
  loading: boolean;
  error: string | null;
  refreshEducation: () => Promise<void>;
  addEducation: (
    education: Omit<Education, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editEducation: (
    id: string,
    education: Partial<Omit<Education, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeEducation: (id: string) => Promise<void>;
  reorderEducation: (
    reorderedEducation: { id: string; order: number }[]
  ) => Promise<void>;
}

const EMPTY: Education[] = [];

export const useEducation = (): EducationContextType => {
  const queryClient = useQueryClient();
  const { data, isPending, error, refetch } = useQuery(educationQuery);

  const setEducation = (update: (prev: Education[]) => Education[]) =>
    queryClient.setQueryData<Education[]>(educationQuery.queryKey, (prev) =>
      update(prev ?? [])
    );

  const refreshEducation = async () => {
    await refetch();
  };

  const addEducation = async (
    educationData: Omit<Education, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-add")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    const id = await withFirebaseRetry(async () =>
      createEducation(educationData)
    );

    setEducation((prev) => [
      ...prev,
      { ...educationData, id, createdAt: Timestamp.now(), updatedAt: Timestamp.now() },
    ]);

    return id;
  };

  const editEducation = async (
    id: string,
    educationData: Partial<Omit<Education, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-edit")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => updateEducation(id, educationData));

    setEducation((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, ...educationData, updatedAt: Timestamp.now() }
          : item
      )
    );
  };

  const removeEducation = async (id: string): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-remove")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () => deleteEducation(id));
    setEducation((prev) => prev.filter((item) => item.id !== id));
  };

  const reorderEducation = async (
    reorderedEducation: { id: string; order: number }[]
  ): Promise<void> => {
    // Rate limit the operation
    if (!globalRateLimiter.isAllowed("education-reorder")) {
      throw new Error(
        "Rate limit exceeded for education operations. Please try again later."
      );
    }

    await withFirebaseRetry(async () =>
      updateEducationOrder(reorderedEducation)
    );

    // For reorder operations, we need to refresh to get the correct order
    await refreshEducation();
  };

  return {
    education: data ?? EMPTY,
    loading: isPending,
    error: error ? "Failed to load education data. Please try again." : null,
    refreshEducation,
    addEducation,
    editEducation,
    removeEducation,
    reorderEducation,
  };
};

// Data lives in the TanStack Query cache; the provider is kept so the app tree doesn't change.
export const EducationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => <>{children}</>;
